export const en = {
  app: {
    title: 'Temporary mail',
    subtitle: 'Protect your inbox from spam — use temporary mailboxes to sign up on websites and services',
    subtitleCount:
      'Protect your inbox from spam — use temporary mailboxes to sign up on websites, create up to {{count}} $t(plural.mailboxGen)',
    back: 'Back',
  },
  plural: {
    mailbox_one: 'mailbox',
    mailbox_other: 'mailboxes',
    mailboxGen_one: 'mailbox',
    mailboxGen_other: 'mailboxes',
    message_one: 'message',
    message_other: 'messages',
    newAdj_one: 'new',
    newAdj_other: 'new',
    attachment_one: 'attachment',
    attachment_other: 'attachments',
    day_one: 'day',
    day_other: 'days',
    dayGen_one: 'day',
    dayGen_other: 'days',
  },
  nav: {
    account: 'Main account',
  },
  auth: {
    login: 'Log in',
    logout: 'Log out',
    loggingIn: 'Logging in…',
    notAuthenticated: 'You are not signed in',
    loginPrompt: 'Sign in with Keycloak to manage your mailboxes.',
    error: 'Could not sign in. Please try again.',
  },
  profile: {
    title: 'Profile',
    email: 'Email',
    username: 'Username',
    subject: 'Subject (sub)',
  },
  subscriptions: {
    selectTitle: 'Choose a subscription',
    selectSubtitle:
      'Your account has several “Temporary mail” subscriptions. Choose the one you want to continue with.',
    selectSubtitleEmpty:
      'No “Temporary mail” subscription is linked to your account yet.',
    continue: 'Continue',
    switch: 'Switch subscription',
    one: 'Subscription',
    active: 'Current subscription',
    unnamed: 'Unnamed subscription',
    defaultName: 'Temporary mail {{n}}',
    renameTitle: 'Edit name',
    renameField: 'Name',
    renameHint: 'No more than {{max}} characters',
    renameRequired: 'Enter a name',
    renameFailed: 'Could not save the name',
    cardTitle: 'Subscription card',
    changePlanWithPartner: 'To change the plan, contact your partner.',
    openMailboxes: 'Go to mailboxes',
    statMailboxes: 'Mailboxes',
    statUsed: 'Used',
    statFree: 'Free of {{limit}} MB',
    statForwarded: 'With forwarding',
    statOf: 'of',
    statMb: 'MB',
    empty: 'No subscriptions available. Connect the service in the partner cabinet or refresh the page.',
    loadError: 'Failed to load subscriptions',
    deeplinkDenied:
      'The link points to a subscription that is not available to your account. Pick a subscription from the list below.',
    deeplinkUnavailable:
      'We could not check whether the subscription from the link is available. Try again later or pick one from the list below.',
    status: {
      active: 'Active',
      suspended: 'Suspended',
      pending_cancellation: 'Pending cancellation',
      terminated: 'Terminated',
    },
  },
  notifications: {
    title: 'Notifications',
    empty: 'No new notifications',
    clear: 'Clear',
    newMessage: 'New message',
    noSubject: 'No subject',
    lifecycle: {
      mailbox: {
        expiring: {
          title: 'Activation is expiring',
          body: 'Mailbox {{address}} will be deactivated in {{minutes}} min',
        },
        size: {
          threshold: {
            title: 'Mailbox almost full',
            body: 'Mailbox {{address}} is {{percent}}% full',
          },
        },
      },
      subscription: {
        quota: {
          warning: {
            title: 'Traffic limit is close',
            body: 'Used {{used}} of {{limit}} MB, {{remaining}} MB left',
          },
          reached: {
            title: 'Traffic limit reached',
            body: 'Used {{used}} of {{limit}} MB. Incoming mail paused, mailboxes deactivated',
          },
        },
      },
    },
  },
  common: {
    yes: 'Yes',
    no: 'No',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading…',
    error: 'An error occurred',
    retry: 'Retry',
    copy: 'Copy address',
    copied: 'Copied',
    showMore: 'Show more',
    selectAll: 'Select all',
    close: 'Close',
  },
  theme: { toggle: 'Toggle theme', light: 'Light', dark: 'Dark' },
  mailbox: {
    pageTitle: 'Temporary mail',
    headerCard: 'Mailboxes',
    addMailbox: '+ Add mailbox',
    quotaRemaining: '{{remaining}} of {{total}} MB left {{period}}',
    quotaTooltip: 'Incoming mail limit is {{total}} MB {{period}}. {{reset}}',
    limitReached: 'Mailbox limit reached',
    limitTooltip: 'You reached the mailbox limit. Delete at least one to create a new mailbox',
    limitTooltipBefore: 'You reached the mailbox limit. To create a new one, ',
    limitTooltipAction: 'delete',
    limitTooltipAfter: ' at least one existing mailbox',
    forwardingBanner: 'Set up automatic forwarding to your personal mailbox',
    forwardingBannerActive: 'Forwarding mail from selected mailboxes to {{email}}',
    forwardingSetup: 'Set up',
    sectionPinned: 'Pinned',
    sectionAll: 'All mailboxes',
    restoreDeleted: 'Restore deleted',
    noNew: 'No new mail',
    newCount: '{{count}} $t(plural.newAdj)',
    forwardedHint: 'Mail goes to the primary address',
    emptyTitle: 'No mailboxes yet',
    emptySubtitle: 'Create your first temporary mailbox',
    emptySubtitleRestore: 'Create your first mailbox or restore old ones',
    emptyRestore: '↺ Restore old mailboxes',
    menu: {
      pin: 'Pin',
      unpin: 'Unpin',
      activate: 'Activate',
      enableForwarding: 'Enable forwarding',
      disableForwarding: 'Disable forwarding',
      setupForwarding: 'Set up forwarding',
      delete: 'Delete mailbox',
    },
    create: {
      title: 'Create mailbox',
      subtitle: 'Make up an address yourself or generate it automatically',
      aliasLabel: 'Enter address',
      aliasPlaceholder: 'e.g. dobryy-kot-24',
      domainLabel: 'Domain',
      regenerate: 'Generate another',
      descriptionPlaceholder: 'Add a mailbox description',
      submit: 'Create',
      checking: 'Checking address…',
      errors: {
        empty: 'Enter a mailbox address',
        already_exists: 'This address is already taken',
        frozen: 'This address will be available again on {{date}}',
        in_trash: 'This address is in your trash. Restore that mailbox or choose another address',
        in_trash_until:
          'This address is in your trash (restorable until {{date}}). Restore that mailbox or choose another address',
        regex_invalid: 'Address may contain only latin letters, digits, dot and hyphen (3–64 chars)',
        descriptionTooLong: 'Description must be at most {{max}} characters',
        quota: 'Limit reached — {{count}} $t(plural.mailbox). Delete unused ones.',
        system: 'An error occurred while creating the mailbox. Please try again.',
      },
    },
    delete: {
      title: 'Delete mailbox {{address}}?',
      subtitle: 'You can restore it within {{count}} $t(plural.dayGen) after deletion',
      confirm: 'Delete',
    },
    restore: {
      title: 'Restore mailboxes',
      subtitle: 'You can restore mailboxes deleted no earlier than {{count}} $t(plural.dayGen) ago',
      listHint: 'Old mailbox names:',
      limitInfo: 'Free restore slots: {{count}} of {{limit}}',
      limitTooltip:
        'Limited by the active mailbox limit: used slots are unavailable and extra checkboxes are disabled',
      duplicateDisabled: 'This address is already selected for restore in another mailbox',
      empty: 'No deleted mailboxes',
      submit: 'Restore',
    },
    bulkCleanup: {
      title: 'Delete unused mailboxes',
      subtitle: 'We found mailboxes with no mail or unused for a long time',
      submit: 'Delete',
    },
    description: {
      title: 'Mailbox description',
      placeholder: 'Add a description',
      add: 'Add description',
    },
    toasts: {
      created: 'Mailbox created',
      createError: 'Mailbox creation error',
      createErrorBody: 'An error occurred while creating the mailbox. Please try again.',
      deleted: 'Mailbox deleted',
      restored: 'Mailbox restored',
      pinned: 'Mailbox pinned',
      unpinned: 'Mailbox unpinned',
      pinLimitTitle: 'Limit reached',
      pinLimitBody: 'You can pin no more than 3 mailboxes. Unpin one to pin a new mailbox',
      activated: 'Mailbox activated',
      extended: 'Active time extended',
      forwardingOn: 'Forwarding enabled',
      forwardingOff: 'Forwarding disabled',
      restorePartial: 'Restored {{ok}} of {{total}}. Some addresses are already taken',
      restoreError: 'Failed to restore mailboxes',
    },
  },
  message: {
    activateMailbox: 'Activate mailbox',
    extendMailbox: 'Extend',
    emptyTitle: 'No mail yet',
    emptySubtitle: 'New mail will appear here',
    trashEmptyTitle: 'Trash is empty',
    trashEmptySubtitle: 'Deleted mail will appear here',
    forwardedEmptyTitle: 'Mail goes to the primary address',
    forwardedEmptySubtitle: 'While forwarding is on, incoming mail for this mailbox is not shown here',
    trashRetentionHint:
      'Messages kept in the trash for more than {{count}} $t(plural.dayGen) are deleted automatically',
    attachments: '{{count}} $t(plural.attachment)',
    tabs: {
      inbox: 'Inbox',
      trash: 'Trash',
    },
    select: 'Select',
    selectAll: 'Select all',
    selectCancel: 'Cancel',
    restore: 'Restore',
    bulk: {
      delete: 'Delete',
      deleteForever: 'Delete forever',
      read: 'Read',
      restore: 'Restore',
    },
    purge: {
      title: 'Permanently delete {{count}} $t(plural.message)?',
      subtitle:
        'These messages will be removed for good: they cannot be restored or opened by link. The freed space becomes available again for receiving new mail.',
      confirm: 'Delete forever',
    },
    menu: {
      enableForwarding: 'Enable forwarding',
      disableForwarding: 'Disable forwarding',
      setupForwarding: 'Set up forwarding',
      markAllRead: 'Mark all as read',
      deleteAll: 'Delete all mail',
      deleteMailbox: 'Delete mailbox',
    },
    detail: {
      attachmentsTitle: 'Attachments',
      downloadAll: 'Download all',
      download: 'Download',
      delete: 'Delete message',
      noSubject: '(no subject)',
    },
    toasts: {
      deleted: 'Message deleted',
      deletedAll: 'All mail deleted',
      markedAllRead: 'All mail marked as read',
      restored: 'Message restored',
      bulkDeleted: 'Deleted {{count}} $t(plural.message)',
      bulkRead: 'Marked as read: {{count}} $t(plural.message)',
      bulkRestored: 'Restored {{count}} $t(plural.message)',
      bulkPurged: 'Permanently deleted: {{count}} $t(plural.message)',
    },
  },
  status: {
    active: 'Active',
    activeSoon: 'Active for {{time}}',
    activeFemale: 'Active',
    activeSoonFemale: 'Active for {{time}}',
    inactive: 'Inactive',
    forwarding: 'Forwarding',
  },
  forwarding: {
    title: 'Forwarding settings',
    subtitle: 'Manage forwarding — confirm your primary email and choose the mailboxes to forward from',
    primaryTitle: 'Primary email address',
    primaryEmptyHint:
      'Specify and confirm the primary email address mail will be forwarded to from active mailboxes',
    primarySetHint: 'We forward mail from selected mailboxes to {{email}}',
    addPrimary: '+ Add mailbox',
    changePrimary: 'Change email',
    unbindPrimary: 'Unbind email',
    pending: 'Pending confirmation',
    enterCode: 'Enter code',
    manageTitle: 'Forwarding management',
    manageHint:
      'Choose mailboxes to forward to your primary email (forwarding only works for active mailboxes)',
    manageDisabledHint:
      'First add and confirm your primary email — after that you can enable forwarding for mailboxes',
    otp1: {
      title: 'Step 1. Enter your primary email',
      subtitle:
        'Enter the email address to forward incoming mail to — we will send a confirmation code to it',
      emailPlaceholder: 'Enter email address',
      submit: 'Get code',
      invalid: 'Enter a valid email address',
    },
    otp2: {
      title: 'Step 2. Enter the code from the email',
      subtitle: 'Enter the confirmation code from the email sent to {{email}}',
      submit: 'Confirm',
      resend: 'Resend code',
      wrong: 'Wrong code. Attempts left: {{attempts}}',
      invalid: 'Invalid confirmation code',
      expired: 'The code has expired. Request a new one',
      expiresIn: 'Code is valid for {{time}}',
    },
    success: { title: 'Email confirmed', goToMailboxes: 'Go to mailboxes' },
    change: {
      title: 'Change primary email {{email}}?',
      subtitle: 'Forwarding from all mailboxes will be disabled',
      confirm: 'Change',
    },
    unbind: {
      title: 'Unbind primary email {{email}}?',
      subtitle: 'Forwarding from all mailboxes will be disabled',
      confirm: 'Unbind',
    },
    toasts: {
      confirmed: 'Forwarding to {{email}} is set up',
      on: 'Forwarding for {{address}} to {{email}} enabled',
      off: 'Forwarding for {{address}} to {{email}} disabled',
      unbound: 'Primary email unbound',
      otpSent: 'Confirmation code re-sent',
    },
  },
  quota: {
    period: { day: 'per day', month: 'this month' },
    periodReset: {
      day: 'The limit resets in a day',
      month: 'It resets on the 1st of next month',
    },
    warningTitle: 'Traffic limit is running out',
    warningBody: 'Less than 5 MB left for new mail. Heavy new mail may not be delivered',
    pausedTitle: 'New mail reception paused until {{date}}',
    resetInTimer: 'The limit resets in {{timer}}, and active mailboxes will receive mail again',
    resetInDays: 'The limit resets in {{count}} $t(plural.day), and active mailboxes will receive mail again',
    pausedDetails: 'Details',
    pausedModal: {
      title: 'New mail reception paused until {{date}}',
      subtitle: '{{reset}}.',
      canTitle: 'What you can do:',
      can: 'Read already received mail\nCreate new mailboxes\nCopy mailbox addresses\nFree up space by permanently deleting mail from trash',
      pausedColTitle: 'What is paused:',
      pausedCol: 'Receiving new incoming mail',
      noteTitle: 'How to free up space',
      note: 'We count the volume of mail received during the period. Messages in trash still take up space — permanently delete unneeded mail from trash and the freed space immediately becomes available for new mail. The limit also fully resets at the start of the next period',
    },
  },
  onboarding: {
    common: {
      tourName: 'Temporary mail',
      counter: '{{current}} of {{total}}',
      next: 'Next',
      done: 'Done',
      close: 'Close the tour',
      replay: 'Show tips',
    },
    tempmailMain: {
      mailboxCreateFirst: {
        title: 'Creating mailboxes',
        body: 'Create your first address. You can have up to {{limit}} mailboxes. Keep in mind: each one actively receives mail for {{minutes}} minutes only, and then goes to sleep.',
      },
      mailboxCreate: {
        title: 'Creating mailboxes',
        body: 'Here you can create new mailboxes. You can create up to {{limit}} addresses. Keep in mind: each one actively receives mail for {{minutes}} minutes only, and then goes to sleep.',
      },
      quotaTraffic: {
        title: 'Traffic usage',
        body: '{{traffic}} MB is the volume of incoming mail. Deleting old messages does not give the megabytes back (just like mobile data). {{reset}}.',
      },
      forwardingIntro: {
        title: 'Mail forwarding',
        body: 'Enter your main mailbox and confirm it with a code — this is a one-time step so the service knows where to send mail.',
      },
      mailboxManage: {
        title: 'Managing a mailbox',
        body: 'Here you can set up your mailbox — pin it, enable or disable forwarding to your main address, activate or delete it.',
      },
    },
  },
  errors: {
    notFoundTitle: 'Page not found',
    notFoundSubtitle: 'You may have followed an outdated link',
    notFoundCta: 'Home',
    genericTitle: 'Something went wrong',
    genericSubtitle: 'We are already aware of the problem and working on it',
    genericCta: 'Try again',
    rateLimited: 'Too many requests. Please try again later',
    unavailable: 'Service is temporarily unavailable',
  },
} as const;
